/**
 * @license
 * Copyright 2026 okayest! studios LLC. All Rights Reserved.
 */

import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import '../../components/circle-button';
import { incrementCounter } from '../data/counter-store';
import { vibrate } from '../common/haptics';

/**
 * Compact counter in the counter list, with its own +/- buttons. Fires
 * `open-counter` with the counter ID when tapped.
 */
@customElement('yl-counter-row')
export class CounterRow extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      border-radius: 12px;
      cursor: pointer;
      transition: background-color 120ms ease;
    }

    .row:hover {
      background: var(--yl-hover-background);
    }

    :host([highlight]) .row {
      background: var(--yl-highlight-background);
    }

    .details {
      flex: 1;
      min-width: 0;
      text-align: center;
      padding: 4px 0;
    }

    .count {
      font-family: var(--yl-headline-font);
      font-size: 2.25rem;
      line-height: 1.2;
      font-variant-numeric: tabular-nums;
    }

    .label {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
      font-size: 0.8rem;
      color: var(--yl-secondary-text);
      white-space: nowrap;
    }

    .label span {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .label wa-icon {
      font-size: 0.7rem;
      flex: none;
    }
  `;

  @property({ attribute: 'counter-id' })
  counterId: string = '';

  @property({ attribute: 'label' })
  label: string = '';

  @property({ attribute: 'count', type: Number })
  count: number = 0;

  @property({ attribute: 'repeats', type: Number })
  repeats: number = 0;

  @property({ attribute: 'repeating', type: Boolean })
  repeating: boolean = false;

  @property({ attribute: 'highlight', type: Boolean, reflect: true })
  highlight: boolean = false;

  private _increment(delta: number) {
    if (incrementCounter(this.counterId, delta)) vibrate();
  }

  private _open() {
    this.dispatchEvent(
      new CustomEvent<string>('open-counter', { detail: this.counterId, bubbles: true, composed: true }),
    );
  }

  render() {
    return html`<div class="row" @click=${this._open}>
      <yl-circle-button
        icon="minus"
        label="Decrement ${this.label}"
        size="medium"
        activate-on-press
        @activate=${() => this._increment(-1)}
      ></yl-circle-button>
      <div class="details">
        <div class="count">${this.count}</div>
        <div class="label">
          <span>${this.label}</span>
          ${this.repeating ? html` • <wa-icon name="retweet"></wa-icon>${this.repeats}` : null}
        </div>
      </div>
      <yl-circle-button
        icon="plus"
        label="Increment ${this.label}"
        size="medium"
        primary
        activate-on-press
        @activate=${() => this._increment(1)}
      ></yl-circle-button>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yl-counter-row': CounterRow;
  }
}
